/**
 * User departments and the notification switches each one starts with.
 *
 * ``UserProfile.department`` is stored as the lowercase ``value``; the UI shows
 * the ``label`` (``purchasing`` reads as "Procurement"). The backend mirror of
 * {@link DEPARTMENT_NOTIFY_DEFAULTS} lives in
 * ``backend/core/services/notification_defaults.py`` — keep the two in step.
 *
 * Example:
 * ```ts
 * departmentLabel('purchasing'); // 'Procurement'
 * notifyDefaultsFor('quality');  // { notify_procurement: false, notify_quality: true, notify_compliance: true }
 * ```
 */

export type DepartmentValue =
  | 'admin'
  | 'purchasing'
  | 'quality'
  | 'finance'
  | 'operations'
  | 'logistics'
  | 'engineering';

export interface DepartmentOption {
  value: DepartmentValue;
  label: string;
}

export const DEPARTMENTS: DepartmentOption[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'purchasing', label: 'Procurement' },
  { value: 'quality', label: 'Quality' },
  { value: 'finance', label: 'Finance' },
  { value: 'operations', label: 'Operations' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'engineering', label: 'Engineering' },
];

/** The three per-site notification switches a department pre-sets. */
export interface NotifyFlags {
  notify_procurement: boolean;
  notify_quality: boolean;
  /** Shown as "Documents": supplier uploads and Read & Accept requests. */
  notify_compliance: boolean;
}

const ALL_OFF: NotifyFlags = {
  notify_procurement: false,
  notify_quality: false,
  notify_compliance: false,
};

/** Starting switches per department; any other department, or none, starts all off. */
export const DEPARTMENT_NOTIFY_DEFAULTS: Partial<Record<DepartmentValue, NotifyFlags>> = {
  admin: { notify_procurement: true, notify_quality: true, notify_compliance: true },
  purchasing: { notify_procurement: true, notify_quality: false, notify_compliance: false },
  quality: { notify_procurement: false, notify_quality: true, notify_compliance: true },
};

/**
 * Stored department value for ``value``, accepting a label too.
 *
 * ``UserProfile.department`` is free text (a users import can say
 * "Procurement" or "Admin (Full Access)"); mirrors the backend's
 * ``department_key``.
 *
 * @param value - department as stored or as labelled (any case, may be blank).
 * @returns The lowercase stored value ('' when blank).
 */
export function departmentKey(value: string | null | undefined): string {
  const key = (value ?? '').trim().toLowerCase();
  if (key === 'admin (full access)') return 'admin';
  const byLabel = DEPARTMENTS.find((d) => d.label.toLowerCase() === key);
  return byLabel ? byLabel.value : key;
}

/**
 * Display label for a stored department value.
 *
 * @param value - ``UserProfile.department`` as stored (any case, may be blank).
 * @returns The UI label, the raw value for a department outside the list, or '' when blank.
 */
export function departmentLabel(value: string | null | undefined): string {
  const key = departmentKey(value);
  if (!key) return '';
  const match = DEPARTMENTS.find((d) => d.value === key);
  return match ? match.label : (value ?? '').trim();
}

/**
 * Notification switches a new user in ``department`` should start with.
 *
 * @param department - stored department value (any case, may be blank).
 * @returns A fresh {@link NotifyFlags} object; all off for unmapped departments.
 */
export function notifyDefaultsFor(department: string | null | undefined): NotifyFlags {
  const key = departmentKey(department) as DepartmentValue;
  return { ...(DEPARTMENT_NOTIFY_DEFAULTS[key] ?? ALL_OFF) };
}
